import React from 'react';
import { getContext } from 'common';
import TextInput, { TextInputProps } from 'components/TextInput';
import _merge from 'lodash/merge';
import Styled, { GlobalCCFKSearchHighlightStyle } from './styles';

export interface FilterFieldContextInterface {
  variant?: 'underlined' | 'outlined';
}

const FilterFieldContext = React.createContext<FilterFieldContextInterface | null>({});

export interface FilterFieldProps extends Omit<React.ComponentPropsWithRef<'div'>, 'onChange'> {
  /** 'value' of the input element -- See TextInput component for more information. */
  value: string;
  /** The FilterField styling variant. */
  variant?: 'outlined' | 'underlined';
  /** 'onChange' callback -- See TextInput component for more information. */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  /** TextInput props. See TextInput component for more information. */
  textInputProps?: Partial<TextInputProps>;
  /** Render search icon. Place <FilterFieldIcon /> sub-component here. */
  renderIcon?: React.ReactNode;
  /** Render clear button. Place <FilterFieldClearButton /> sub-component here. */
  renderClearButton?: React.ReactNode;
}

/** FilterField component, use as a filter or search.  */
const FilterField = React.forwardRef<HTMLElement, FilterFieldProps>((props: FilterFieldProps, ref) =>
  getContext(({ RTL }) => {
    const {
      variant,
      textInputProps,
      renderIcon,
      renderClearButton,
      value,
      onChange,
      ...otherProps
    } = props;
    const { inputProps = {}, ...otherTextInputProps } = textInputProps;
    const { style: inputStyle, ...otherInputProps } = inputProps;
    const TEXTINPUT_PROPS = {
      placeholder: 'Placeholder',
      variant,
      style: {
        height: '100%',
        alignSelf: 'flex-end'
      }
    };

    const resolvedTextInputProps = _merge({}, TEXTINPUT_PROPS, otherTextInputProps);

    /** inputProps cannot be put in _merge as the ref passed in it will be lost */
    const resolvedInputProps = {
      style: {
        height: '3rem',
        width: '100%',
        paddingLeft: renderIcon ? variant === 'underlined' ? '2rem' : '2.5rem' : undefined,
        paddingRight: renderClearButton ? variant === 'underlined' ? '2rem' : '2.5rem' : undefined,
        ...(RTL && {
          paddingLeft: renderClearButton ? variant === 'underlined' ? '2rem' : '2.5rem' : undefined,
          paddingRight: renderIcon ? variant === 'underlined' ? '2rem' : '2.5rem' : undefined,
        }),
        ...inputStyle,
      },
      ...otherInputProps
    };
    return (
      <Styled.FilterField
        $rtl={RTL}
        ref={ref}
        {...otherProps}
      >
        <FilterFieldContext.Provider value={{ variant }}>
          <GlobalCCFKSearchHighlightStyle />
          {renderIcon}
          <TextInput
            value={value}
            onChange={onChange}
            inputProps={resolvedInputProps}
            {...resolvedTextInputProps}
          />
          {renderClearButton}
        </FilterFieldContext.Provider>
      </Styled.FilterField>
    );
  }));

FilterField.displayName = 'FilterField';
FilterField.defaultProps = {
  variant: 'underlined',
  onChange: undefined,
  renderIcon: undefined,
  renderClearButton: undefined,
  textInputProps: undefined
};

/* Exports */
export { FilterField as default, FilterFieldContext };
